import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';

interface ReviewItem {
  id: string;
  title: string;
  status: string;
  due_date: string | null;
  priority: string;
  notes: string | null;
  completed_at: string | null;
}

const STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
];

const STATUS_COLORS: Record<string, string> = {
  pending:     'bg-gray-700 text-gray-300',
  in_progress: 'bg-yellow-900/50 text-yellow-300',
  completed:   'bg-green-900/50 text-green-300',
};

export default function ActionItemReviewPage() {
  const { token } = useParams<{ token: string }>();

  const [clientName, setClientName] = useState('');
  const [sessionLabel, setSessionLabel] = useState('');
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  const fetchItems = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.review(token)}`);
      if (res.status === 401) { setExpired(true); return; }
      if (!res.ok) { setExpired(true); return; }
      const data = await res.json();
      setClientName(data.client_first_name || '');
      setSessionLabel(data.session_label || '');
      setItems(data.action_items || []);
      const drafts: Record<string, string> = {};
      (data.action_items || []).forEach((i: ReviewItem) => {
        drafts[i.id] = i.notes || '';
      });
      setNotesDraft(drafts);
    } catch {
      setExpired(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const updateItem = async (itemId: string, patch: { status?: string; notes?: string }) => {
    if (!token) return;
    setSavingId(itemId);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.actionItems.reviewUpdate(token, itemId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        }
      );
      if (!res.ok) return;
      const updated = await res.json();
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updated } : i));
      setSavedId(itemId);
      setTimeout(() => setSavedId(id => id === itemId ? null : id), 2000);
    } finally {
      setSavingId(null);
    }
  };

  const handleStatusChange = (item: ReviewItem, newStatus: string) => {
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
    updateItem(item.id, { status: newStatus });
  };

  const handleNotesSave = (item: ReviewItem) => {
    updateItem(item.id, { notes: notesDraft[item.id] ?? '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-canvas flex items-center justify-center">
        <p className="text-gray-400">Loading your action items…</p>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-dark-canvas flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="text-4xl mb-4">🔗</div>
          <h1 className="text-2xl font-bold text-white mb-3">Link expired or invalid</h1>
          <p className="text-gray-400">This action item link has expired or is no longer valid. Ask your coach to resend it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-canvas py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-violet-400 font-semibold text-sm tracking-wide uppercase">CoachSight</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Hi {clientName},</h1>
          <p className="text-gray-400 mt-1">
            Your action items from <span className="text-gray-200">{sessionLabel}</span>
          </p>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No action items shared yet.</div>
        ) : (
          <div className="flex flex-col gap-4">
            {items.map(item => (
              <div
                key={item.id}
                className={`bg-dark-surface border rounded-xl p-5 transition-colors ${
                  item.status === 'completed' ? 'border-green-700/40' : 'border-dark-border'
                }`}
              >
                {/* Title row */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h2 className={`font-semibold text-base leading-snug ${
                    item.status === 'completed' ? 'line-through text-gray-500' : 'text-white'
                  }`}>
                    {item.title}
                  </h2>
                  {item.due_date && (
                    <span className="text-xs text-gray-500 whitespace-nowrap mt-0.5">
                      Due {item.due_date}
                    </span>
                  )}
                </div>

                {/* Status selector */}
                <div className="mb-4">
                  <label className="block text-xs text-gray-500 mb-1.5">Status</label>
                  <div className="flex gap-2 flex-wrap">
                    {STATUS_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleStatusChange(item, opt.value)}
                        disabled={savingId === item.id}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                          item.status === opt.value
                            ? `${STATUS_COLORS[opt.value]} border-transparent`
                            : 'border-dark-border text-gray-500 hover:text-gray-300 hover:border-gray-600'
                        } disabled:opacity-50`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Notes</label>
                  <textarea
                    rows={2}
                    value={notesDraft[item.id] ?? ''}
                    onChange={e => setNotesDraft(prev => ({ ...prev, [item.id]: e.target.value }))}
                    onBlur={() => handleNotesSave(item)}
                    placeholder="Add a note…"
                    className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  />
                </div>

                {/* Save indicator */}
                {savedId === item.id && (
                  <p className="text-xs text-green-400 mt-2">Saved</p>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-gray-600 mt-10">
          Updates are saved automatically. Powered by CoachSight.
        </p>
      </div>
    </div>
  );
}
